import { useEffect, useState } from "react";
import { X, Plus, Trash2, Search, Percent, Euro, Info, Loader2, Upload } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import VademecumImportModal from "./VademecumImportModal";
import LabAnalysisPanel from "./LabAnalysisPanel";

/**
 * Modal para configurar la condición comercial de UN proveedor:
 *  - has_agreement
 *  - descuento medio % (sobre PVL)
 *  - volumen mensual mínimo (€)
 *  - vademecum: lista de {gh, discount_pct} con buscador para añadir
 *
 * Props:
 *   laboratorio: string
 *   onClose: () => void
 *   onSaved: () => void  // se llama tras guardar/borrar entradas
 */
export default function SupplierConditionsModal({ laboratorio, onClose, onSaved }) {
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [newPct, setNewPct] = useState("");
  const [selectedGh, setSelectedGh] = useState(null);
  const [showImport, setShowImport] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/supplier-agreements");
      const item = (r.data?.items || []).find((x) => x.laboratorio === laboratorio);
      setAgreement(item || {
        laboratorio,
        has_agreement: false,
        discount_avg_pct: 0,
        linear_discount_pct: 0,
        min_monthly_volume_eur: 0,
        annual_rappel_enabled: false,
        annual_rappel_threshold_eur: 0,
        annual_rappel_extra_pct: 0,
        annual_rappel_period: "annual",
        annual_rappel_start_date: "",
        vademecum: [],
        exceptions: [],
        no_discount_ghs: [],
        no_discount_cns: [],
        note: "",
      });
    } catch (e) {
      toast.error("No se pudo cargar la condición");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [laboratorio]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/supplier-agreements", {
        laboratorio,
        has_agreement: !!agreement.has_agreement,
        discount_avg_pct: Number(agreement.discount_avg_pct) || 0,
        linear_discount_pct: Number(agreement.linear_discount_pct) || 0,
        min_monthly_volume_eur: Number(agreement.min_monthly_volume_eur) || 0,
        annual_rappel_enabled: !!agreement.annual_rappel_enabled,
        annual_rappel_threshold_eur: Number(agreement.annual_rappel_threshold_eur) || 0,
        annual_rappel_extra_pct: Number(agreement.annual_rappel_extra_pct) || 0,
        annual_rappel_period: agreement.annual_rappel_period || "annual",
        annual_rappel_start_date: agreement.annual_rappel_start_date || "",
        exceptions: agreement.exceptions || [],
        note: agreement.note || "",
      });
      toast.success("Condición guardada");
      onSaved?.();
    } catch (e) {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  // Búsqueda de GH para añadir al vademecum (debounced)
  useEffect(() => {
    if (!laboratorio) return;
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const r = await api.get(`/supplier-agreements/${encodeURIComponent(laboratorio)}/gh-search`, {
          params: { q: searchQ },
        });
        setSearchResults(r.data?.results || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [searchQ, laboratorio, agreement?.vademecum?.length]);

  const addVademecum = async () => {
    if (!selectedGh || !newPct) {
      toast.error("Elige una molécula y un %");
      return;
    }
    try {
      await api.post(`/supplier-agreements/${encodeURIComponent(laboratorio)}/vademecum`, {
        gh: selectedGh,
        discount_pct: Number(newPct),
      });
      toast.success(`Vademecum: ${selectedGh.slice(0, 40)}… (${newPct}%)`);
      setSelectedGh(null);
      setNewPct("");
      setSearchQ("");
      await load();
      onSaved?.();
    } catch {
      toast.error("No se pudo añadir al vademecum");
    }
  };

  const removeVademecum = async (gh) => {
    if (!window.confirm(`¿Quitar "${gh}" del vademecum?`)) return;
    try {
      await api.delete(`/supplier-agreements/${encodeURIComponent(laboratorio)}/vademecum/${encodeURIComponent(gh)}`);
      toast.success(`Molécula eliminada: ${gh.slice(0, 40)}…`);
      await load();
      onSaved?.();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const removeCnException = async (cn) => {
    if (!window.confirm(`¿Quitar la excepción del CN ${cn}?`)) return;
    try {
      await api.delete(`/supplier-agreements/${encodeURIComponent(laboratorio)}/vademecum-by-cn/${encodeURIComponent(cn)}`);
      toast.success(`Excepción del CN ${cn} eliminada`);
      await load();
      onSaved?.();
    } catch {
      toast.error("No se pudo eliminar la excepción por CN");
    }
  };

  const [cnAddCn, setCnAddCn] = useState("");
  const [cnAddPct, setCnAddPct] = useState("");
  const [cnAddLabel, setCnAddLabel] = useState("");

  const upsertCnException = async () => {
    const cn = (cnAddCn || "").replace(/\D/g, "");
    const pct = Number(cnAddPct);
    if (!cn) { toast.error("CN inválido"); return; }
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) { toast.error("% fuera de rango (0-100)"); return; }
    try {
      await api.post(`/supplier-agreements/${encodeURIComponent(laboratorio)}/vademecum-by-cn`, {
        cn, discount_pct: pct, label: cnAddLabel || "",
      });
      toast.success(`Excepción CN ${cn} · ${pct}% guardada`);
      setCnAddCn(""); setCnAddPct(""); setCnAddLabel("");
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la excepción");
    }
  };

  const [ndAdd, setNdAdd] = useState("");

  const addNoDiscount = async () => {
    const raw = (ndAdd || "").trim();
    if (!raw) { toast.error("Escribe una molécula (G.H.) o un CN"); return; }
    const digits = raw.replace(/\D/g, "");
    const isCn = !/[a-zA-Z]/.test(raw) && digits.length >= 6;
    try {
      await api.post(`/supplier-agreements/${encodeURIComponent(laboratorio)}/no-discount`,
        isCn ? { cn: digits } : { gh: raw.toUpperCase() });
      toast.success(`${raw} marcada sin descuento`);
      setNdAdd("");
      await load();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo añadir");
    }
  };

  const removeNoDiscount = async (value, isCn) => {
    try {
      await api.delete(`/supplier-agreements/${encodeURIComponent(laboratorio)}/no-discount`, {
        params: isCn ? { cn: value } : { gh: value },
      });
      await load();
      onSaved?.();
    } catch (e) {
      toast.error("No se pudo quitar");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
      onClick={onClose}
      data-testid="supplier-conditions-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-slate-200">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Condición de compra</div>
            <h2 className="text-xl font-bold text-slate-900 mt-1">{laboratorio}</h2>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700" data-testid="close-cond-modal">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading || !agreement ? (
          <div className="p-10 grid place-items-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="p-6 space-y-6">
            {/* ----- Condición media + volumen ----- */}
            <div className="space-y-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!agreement.has_agreement}
                  onChange={(e) => setAgreement({ ...agreement, has_agreement: e.target.checked })}
                  className="h-4 w-4 rounded"
                  data-testid="cond-has-agreement"
                />
                <span className="text-sm font-semibold text-slate-700">
                  Tengo condición de compra con este proveedor
                </span>
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                    <Percent className="h-3 w-3" /> Descuento lineal (% sobre PVL)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={agreement.linear_discount_pct ?? 0}
                    onChange={(e) => setAgreement({ ...agreement, linear_discount_pct: e.target.value })}
                    className="mt-1 block w-full rounded-md border-slate-300 px-3 py-2 text-sm font-mono-num"
                    placeholder="0"
                    data-testid="cond-linear-pct"
                  />
                  <p className="mt-1 text-[10px] text-slate-500 italic">
                    Descuento base aplicado a las moléculas que NO tengan un % específico en el vademecum.
                  </p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                    <Percent className="h-3 w-3" /> Descuento medio (% sobre PVL)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={agreement.discount_avg_pct}
                    onChange={(e) => setAgreement({ ...agreement, discount_avg_pct: e.target.value })}
                    className="mt-1 block w-full rounded-md border-slate-300 px-3 py-2 text-sm font-mono-num"
                    placeholder="0"
                    data-testid="cond-avg-pct"
                  />
                  <p className="mt-1 text-[10px] text-slate-500 italic">
                    Se recalcula automáticamente cuando importas el vademecum (media de los descuentos por molécula).
                  </p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                    <Euro className="h-3 w-3" /> Volumen mensual mínimo (€ PVL)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="100"
                    value={agreement.min_monthly_volume_eur}
                    onChange={(e) => setAgreement({ ...agreement, min_monthly_volume_eur: e.target.value })}
                    className="mt-1 block w-full rounded-md border-slate-300 px-3 py-2 text-sm font-mono-num"
                    placeholder="0"
                    data-testid="cond-min-vol"
                  />
                </div>
              </div>

              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3 flex gap-2">
                <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Si no cumples el volumen mensual mínimo, el sistema te avisará en el análisis económico
                  del estudio y en el Cuadro de mando (puedes perder el descuento lineal según el proveedor).
                </span>
              </div>

              {/* ----- Rappel anual ----- */}
              <div className="border border-slate-200 rounded-md p-4 bg-slate-50 space-y-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!agreement.annual_rappel_enabled}
                    onChange={(e) => setAgreement({ ...agreement, annual_rappel_enabled: e.target.checked })}
                    className="h-4 w-4 rounded"
                    data-testid="cond-rappel-enabled"
                  />
                  <span className="text-sm font-semibold text-slate-700">
                    Este proveedor ofrece rappel por volumen
                  </span>
                </label>
                {agreement.annual_rappel_enabled && (
                  <div className="space-y-3">
                    {/* Iter-61: periodicidad del rappel + fecha de inicio (para el anual) */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Periodicidad</label>
                        <div className="mt-1 inline-flex rounded-md border border-slate-300 bg-white p-0.5 text-xs font-semibold" data-testid="cond-rappel-period-switch">
                          <button
                            type="button"
                            onClick={() => setAgreement({ ...agreement, annual_rappel_period: "monthly" })}
                            className={`px-3 py-1.5 rounded ${(agreement.annual_rappel_period || "annual") === "monthly" ? "bg-slate-950 text-white" : "text-slate-600 hover:text-slate-900"}`}
                            data-testid="cond-rappel-period-monthly"
                          >
                            Mensual
                          </button>
                          <button
                            type="button"
                            onClick={() => setAgreement({ ...agreement, annual_rappel_period: "annual" })}
                            className={`px-3 py-1.5 rounded ${(agreement.annual_rappel_period || "annual") === "annual" ? "bg-slate-950 text-white" : "text-slate-600 hover:text-slate-900"}`}
                            data-testid="cond-rappel-period-annual"
                          >
                            Anual
                          </button>
                        </div>
                        <p className="mt-1 text-[10px] text-slate-500 italic">
                          Mensual: se reinicia cada mes. Anual: acumula 12 meses desde la fecha de inicio.
                        </p>
                      </div>
                      {(agreement.annual_rappel_period || "annual") === "annual" && (
                        <div>
                          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Fecha de inicio del año rappel</label>
                          <input
                            type="date"
                            value={agreement.annual_rappel_start_date || ""}
                            onChange={(e) => setAgreement({ ...agreement, annual_rappel_start_date: e.target.value })}
                            className="mt-1 block w-full rounded-md border-slate-300 px-3 py-2 text-sm font-mono-num"
                            data-testid="cond-rappel-start-date"
                          />
                          <p className="mt-1 text-[10px] text-slate-500 italic">
                            Desde esta fecha se acumulan los 12 meses. Déjalo vacío para usar el año natural (1 enero).
                          </p>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                        <Euro className="h-3 w-3" /> Umbral {(agreement.annual_rappel_period || "annual") === "monthly" ? "mensual" : "anual"} (€ PVL)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1000"
                        value={agreement.annual_rappel_threshold_eur ?? 0}
                        onChange={(e) => setAgreement({ ...agreement, annual_rappel_threshold_eur: e.target.value })}
                        className="mt-1 block w-full rounded-md border-slate-300 px-3 py-2 text-sm font-mono-num"
                        placeholder="100000"
                        data-testid="cond-rappel-threshold"
                      />
                      <p className="mt-1 text-[10px] text-slate-500 italic">
                        Compra {(agreement.annual_rappel_period || "annual") === "monthly" ? "mensual" : "anual"} acumulada en PVL a partir de la cual se activa el rappel.
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                        <Percent className="h-3 w-3" /> % adicional al alcanzar
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={agreement.annual_rappel_extra_pct ?? 0}
                        onChange={(e) => setAgreement({ ...agreement, annual_rappel_extra_pct: e.target.value })}
                        className="mt-1 block w-full rounded-md border-slate-300 px-3 py-2 text-sm font-mono-num"
                        placeholder="5"
                        data-testid="cond-rappel-extra-pct"
                      />
                      <p className="mt-1 text-[10px] text-slate-500 italic">
                        % de descuento extra sobre el total {(agreement.annual_rappel_period || "annual") === "monthly" ? "mensual" : "anual"} al alcanzar el umbral.
                      </p>
                    </div>
                  </div>
                  </div>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Nota (opcional)</label>
                <textarea
                  value={agreement.note || ""}
                  onChange={(e) => setAgreement({ ...agreement, note: e.target.value })}
                  className="mt-1 block w-full rounded-md border-slate-300 px-3 py-2 text-sm"
                  rows={2}
                  data-testid="cond-note"
                />
              </div>

              <div className="flex justify-end">
                <button
                  onClick={save}
                  disabled={saving}
                  className="px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-md disabled:opacity-50"
                  data-testid="cond-save"
                >
                  {saving ? "Guardando…" : "Guardar condición"}
                </button>
              </div>
            </div>

            <hr className="border-slate-200" />

            {/* ----- Análisis del lab vs estudio actual ----- */}
            <div>
              <h3 className="font-bold text-slate-900 mb-3">Análisis con el estudio actual</h3>
              <LabAnalysisPanel laboratorio={laboratorio} />
            </div>

            <hr className="border-slate-200" />

            {/* ----- Vademecum (descuentos por molécula) ----- */}
            <div>
              <div className="flex items-start justify-between gap-3 mb-1">
                <h3 className="font-bold text-slate-900">Vademecum · descuentos por molécula</h3>
                <button
                  onClick={() => setShowImport(true)}
                  className="px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-md text-xs font-semibold inline-flex items-center gap-1.5"
                  data-testid="open-import-modal"
                >
                  <Upload className="h-3.5 w-3.5" /> Importar PDF/Excel
                </button>
              </div>
              <p className="text-xs text-slate-500 mb-4">
                Añade aquí los descuentos específicos por molécula que tu proveedor te ofrece.
                Tienen prioridad sobre el descuento medio si son superiores.
              </p>

              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <div className="flex-1 relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchQ}
                    onChange={(e) => { setSearchQ(e.target.value); setSelectedGh(null); }}
                    placeholder="Buscar molécula del lab…"
                    className="block w-full rounded-md border-slate-300 pl-9 pr-3 py-2 text-sm"
                    data-testid="vade-search"
                  />
                  {searchResults.length > 0 && !selectedGh && searchQ && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-y-auto">
                      {searchResults.map((r) => (
                        <button
                          key={r.gh}
                          onClick={() => { setSelectedGh(r.gh); setSearchQ(r.gh); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-sky-50 border-b border-slate-100 last:border-b-0"
                          data-testid={`vade-search-result-${r.gh}`}
                        >
                          {r.gh}
                        </button>
                      ))}
                      {searchLoading && <div className="p-3 text-xs text-slate-500">Buscando…</div>}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={newPct}
                    onChange={(e) => setNewPct(e.target.value)}
                    placeholder="%"
                    className="w-24 rounded-md border-slate-300 px-3 py-2 text-sm font-mono-num"
                    data-testid="vade-new-pct"
                  />
                  <button
                    onClick={addVademecum}
                    disabled={!selectedGh || !newPct}
                    className="px-3 py-2 bg-sky-600 text-white rounded-md text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1"
                    data-testid="vade-add"
                  >
                    <Plus className="h-4 w-4" /> Añadir
                  </button>
                </div>
              </div>

              {agreement.vademecum?.length === 0 ? (
                <div className="text-center text-sm text-slate-500 py-6 bg-slate-50 border border-dashed border-slate-300 rounded-md">
                  Sin moléculas en el vademecum todavía.
                </div>
              ) : (
                <>
                  {/* Contador y medias */}
                  {(() => {
                    const vade = agreement.vademecum || [];
                    const exc = new Set(agreement.exceptions || []);
                    const linear = Number(agreement.linear_discount_pct) || 0;
                    const arith = vade.length
                      ? vade.reduce((s, v) => s + (Number(v.discount_pct) || 0), 0) / vade.length
                      : 0;
                    // Media efectiva: para cada GH del vademécum, aplica la fórmula
                    // (si hay lineal manda salvo excepción; sin lineal usa vademecum).
                    const effList = vade.map((v) => {
                      if (linear > 0) return exc.has(v.gh) ? Number(v.discount_pct) || 0 : linear;
                      return Number(v.discount_pct) || 0;
                    });
                    const eff = effList.length ? effList.reduce((s, x) => s + x, 0) / effList.length : 0;
                    const validExceptions = (agreement.exceptions || []).filter((gh) =>
                      vade.some((v) => v.gh === gh),
                    ).length;
                    return (
                      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs">
                        {linear > 0 && (
                          <span className="inline-flex items-center gap-1.5 bg-violet-50 border border-violet-200 text-violet-800 px-2.5 py-1 rounded-full font-semibold">
                            Descuento lineal {linear}% <span className="text-violet-600">·</span>
                            {" "}{validExceptions} excepción{validExceptions === 1 ? "" : "es"} activ{validExceptions === 1 ? "a" : "as"}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-800 px-2.5 py-1 rounded-full">
                          Media aritmética vademecum: <strong>{arith.toFixed(2).replace(".", ",")}%</strong>
                        </span>
                        <span
                          className="inline-flex items-center gap-1.5 bg-sky-50 border border-sky-200 text-sky-800 px-2.5 py-1 rounded-full"
                          title={linear > 0
                            ? "Media aplicando la fórmula: lineal manda salvo excepciones marcadas."
                            : "Sin lineal: coincide con la media aritmética del vademecum."}
                        >
                          Media efectiva: <strong>{eff.toFixed(2).replace(".", ",")}%</strong>
                        </span>
                      </div>
                    );
                  })()}

                  <div className="border border-slate-200 rounded-md overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3 font-semibold text-slate-700">Molécula (G.H.)</th>
                          <th className="text-right p-3 font-semibold text-slate-700 w-24">Descuento</th>
                          {Number(agreement.linear_discount_pct) > 0 && (
                            <th
                              className="text-center p-3 font-semibold text-slate-700 w-28"
                              title="Marca esta casilla para que este descuento del vademécum se aplique en lugar del lineal (excepción)"
                            >
                              Excepción
                            </th>
                          )}
                          <th className="w-12"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(agreement.vademecum || []).map((v) => {
                          const isException = (agreement.exceptions || []).includes(v.gh);
                          const linear = Number(agreement.linear_discount_pct) || 0;
                          return (
                            <tr
                              key={v.gh}
                              className={`border-t border-slate-200 ${isException && linear > 0 ? "bg-amber-50/60" : ""}`}
                              data-testid={`vade-row-${v.gh}`}
                            >
                              <td className="p-3 text-slate-800">{v.gh}</td>
                              <td className="p-3 text-right font-mono-num font-semibold text-slate-900">
                                {v.discount_pct}%
                              </td>
                              {linear > 0 && (
                                <td className="p-3 text-center">
                                  <label className="inline-flex items-center gap-1.5 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={isException}
                                      onChange={(e) => {
                                        const next = new Set(agreement.exceptions || []);
                                        if (e.target.checked) next.add(v.gh);
                                        else next.delete(v.gh);
                                        setAgreement({ ...agreement, exceptions: Array.from(next) });
                                      }}
                                      className="h-4 w-4 rounded accent-amber-600"
                                      data-testid={`vade-exc-${v.gh}`}
                                    />
                                    {isException && (
                                      <span className="text-[10px] font-bold uppercase text-amber-700">Aplica</span>
                                    )}
                                  </label>
                                </td>
                              )}
                              <td className="p-3 text-right">
                                <button
                                  onClick={() => removeVademecum(v.gh)}
                                  className="text-rose-600 hover:text-rose-800"
                                  title="Quitar del vademecum"
                                  data-testid={`vade-del-${v.gh}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {Number(agreement.linear_discount_pct) > 0 && (
                    <p className="mt-2 text-[10px] text-slate-500 italic">
                      Con descuento lineal activo, el lineal manda por defecto en todas las moléculas. Marca &quot;Excepción&quot;
                      en las que quieras aplicar el % específico del vademécum (aunque sea peor que el lineal). Recuerda pulsar &quot;Guardar cambios&quot; abajo.
                    </p>
                  )}
                </>
              )}
            </div>

            {/* ----- Excepciones por CN ----- */}
            <div className="pt-2" data-testid="cn-exceptions-section">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <h3 className="font-bold text-slate-900">Excepciones por CN</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Descuentos por Código Nacional (CN) para productos que no tienen G.H. resoluble en el catálogo global.
                    Típicamente cargados al importar el vademécum del proveedor (p. ej. Sandoz). También puedes añadirlos o editarlos manualmente.
                  </p>
                </div>
                <span className="text-xs bg-sky-50 text-sky-700 border border-sky-200 px-2 py-1 rounded font-mono-num" data-testid="cn-exceptions-count">
                  {(agreement.vademecum_by_cn || []).length} CN
                </span>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 mb-3 grid grid-cols-1 sm:grid-cols-[140px_100px_1fr_auto] gap-2 items-end">
                <div>
                  <label className="text-[10px] uppercase font-semibold text-slate-500 mb-1 block">CN</label>
                  <input
                    type="text"
                    value={cnAddCn}
                    onChange={(e) => setCnAddCn(e.target.value)}
                    placeholder="p.ej. 705422"
                    className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white font-mono-num"
                    data-testid="cn-exc-add-cn"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-semibold text-slate-500 mb-1 block">% Descuento</label>
                  <input
                    type="number" min="0.1" max="100" step="0.5"
                    value={cnAddPct}
                    onChange={(e) => setCnAddPct(e.target.value)}
                    placeholder="35"
                    className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white font-mono-num"
                    data-testid="cn-exc-add-pct"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-semibold text-slate-500 mb-1 block">Etiqueta (opcional)</label>
                  <input
                    type="text"
                    value={cnAddLabel}
                    onChange={(e) => setCnAddLabel(e.target.value)}
                    placeholder="Nombre del producto"
                    className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
                    data-testid="cn-exc-add-label"
                  />
                </div>
                <button
                  type="button"
                  onClick={upsertCnException}
                  className="inline-flex items-center gap-1.5 bg-sky-600 hover:bg-sky-700 text-white px-3 py-2 rounded-md text-xs font-bold"
                  data-testid="cn-exc-add-btn"
                >
                  <Plus className="h-3.5 w-3.5" /> Añadir
                </button>
              </div>

              {(agreement.vademecum_by_cn || []).length === 0 ? (
                <div className="border border-dashed border-slate-200 rounded-md p-6 text-center text-slate-400 text-sm" data-testid="cn-exceptions-empty">
                  Sin excepciones por CN. Al importar un vademécum con líneas por CN (p. ej. Sandoz sin G.H. resoluble), aparecerán aquí.
                </div>
              ) : (
                <div className="border border-slate-200 rounded-md overflow-hidden">
                  <table className="w-full text-sm" data-testid="cn-exceptions-table">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-3 font-semibold text-slate-700 w-24">CN</th>
                        <th className="text-left p-3 font-semibold text-slate-700">Producto</th>
                        <th className="text-right p-3 font-semibold text-slate-700 w-24">Descuento</th>
                        <th className="w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(agreement.vademecum_by_cn || [])
                        .slice()
                        .sort((a, b) => (b.discount_pct || 0) - (a.discount_pct || 0))
                        .map((v) => (
                          <tr key={v.cn} className="border-t border-slate-200" data-testid={`cn-exc-row-${v.cn}`}>
                            <td className="p-3 font-mono-num font-semibold text-slate-900">{v.cn}</td>
                            <td className="p-3 text-slate-600 text-xs">{v.label || "—"}</td>
                            <td className="p-3 text-right font-mono-num font-semibold text-slate-900">{v.discount_pct}%</td>
                            <td className="p-3 text-right">
                              <button
                                onClick={() => removeCnException(v.cn)}
                                className="text-rose-600 hover:text-rose-800"
                                title="Quitar excepción por CN"
                                data-testid={`cn-exc-del-${v.cn}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ----- Moléculas SIN descuento (0%) ----- */}
            <div className="pt-2" data-testid="no-discount-section">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <h3 className="font-bold text-slate-900">Moléculas sin descuento (0%)</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Moléculas o CN que el laboratorio deja EXCLUIDOS de su tarifa (bloque &quot;SIN DESCUENTO&quot;).
                    Nunca reciben el descuento lineal ni el medio, y el laboratorio no se propone como mejor opción para ellas.
                  </p>
                </div>
                <span className="text-xs bg-rose-50 text-rose-700 border border-rose-200 px-2 py-1 rounded font-mono-num" data-testid="no-discount-count">
                  {(agreement.no_discount_ghs || []).length + (agreement.no_discount_cns || []).length} excluidas
                </span>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 mb-3 flex flex-col sm:flex-row gap-2 sm:items-end">
                <div className="flex-1">
                  <label className="text-[10px] uppercase font-semibold text-slate-500 mb-1 block">Molécula (G.H.) o CN</label>
                  <input
                    type="text"
                    value={ndAdd}
                    onChange={(e) => setNdAdd(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addNoDiscount(); }}
                    placeholder="p.ej. AMIKACINA 500 MG o 705422"
                    className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-sm bg-white"
                    data-testid="no-discount-add-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={addNoDiscount}
                  className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white px-3 py-2 rounded-md text-xs font-bold"
                  data-testid="no-discount-add-btn"
                >
                  <Plus className="h-3.5 w-3.5" /> Marcar sin descuento
                </button>
              </div>

              {((agreement.no_discount_ghs || []).length + (agreement.no_discount_cns || []).length) === 0 ? (
                <div className="border border-dashed border-slate-200 rounded-md p-6 text-center text-slate-400 text-sm" data-testid="no-discount-empty">
                  Sin exclusiones. Al importar una tarifa con bloque &quot;SIN DESCUENTO&quot; se cargarán aquí automáticamente.
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="no-discount-list">
                  {(agreement.no_discount_ghs || []).map((g) => (
                    <span key={`gh-${g}`} className="inline-flex items-center gap-1 text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-2 py-1" data-testid={`no-discount-gh-${g}`}>
                      {g}
                      <button onClick={() => removeNoDiscount(g, false)} className="text-rose-500 hover:text-rose-800" title="Quitar exclusión">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  {(agreement.no_discount_cns || []).map((c) => (
                    <span key={`cn-${c}`} className="inline-flex items-center gap-1 text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-2 py-1 font-mono-num" data-testid={`no-discount-cn-${c}`}>
                      CN {c}
                      <button onClick={() => removeNoDiscount(c, true)} className="text-rose-500 hover:text-rose-800" title="Quitar exclusión">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {showImport && (
        <VademecumImportModal
          laboratorio={laboratorio}
          onClose={() => setShowImport(false)}
          onApplied={() => { setShowImport(false); load(); onSaved?.(); }}
        />
      )}
    </div>
  );
}
